<template>
  <DModal :show-close="false" :isBgOverlay="true" @close="$emit('close')" :appendToBody="true"
          style="z-index: 12312312312312" :placeInTop="true">
    <div class="d-modal text-dark" slot="insertComponent">
      <div class="d-modal__header">
        <h2>Tidsbegrænset undervisningstillæg</h2>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="d-modal__content text-dark">
        <p class="tuv__intro">
          Beregn UV-tillæg for en tidsbegrænset ansættelse ud fra skoledage og UV-tid, og send et fast tillæg
          for perioden direkte til {{ salaryTargetLabel }} på baggrund af beregningen.
        </p>

        <div class="do-form-row">
          <div class="d-input-group">
            <label class="d-label">Vælg ansat</label>
            <select v-model="userId" class="height-34 do-select width-100" @change="onUserChange">
              <option disabled value="">Vælg ansat</option>
              <option v-for="user in users" :key="user.id" :value="user.id">{{ user.name }}</option>
            </select>
          </div>
          <div class="d-input-group" v-if="userId">
            <label class="d-label">Vælg ansættelse</label>
            <select v-model="employmentId" class="height-34 do-select width-100" @change="onEmploymentChange">
              <option :value="null"></option>
              <option v-for="emp in userEmployments" :key="emp.id" :value="emp.id">{{ emp.label }}</option>
            </select>
            <p class="tuv__hint" v-if="userEmployments.length === 0">
              Ingen {{ salaryTargetLabel }}-ansættelse med stillingskategori fundet for personen.
            </p>
          </div>
        </div>

        <template v-if="userId">
          <Loader v-if="isLoading" :isLoaderModal="true"></Loader>
          <template v-else>
            <div class="tuv__facts">
              <div class="tuv__fact">
                <span class="tuv__fact-label">Ansættelsesgrad</span>
                <span class="tuv__fact-value">{{ ansaettelsesgradPercent != null ? formatPercent(ansaettelsesgradPercent) : '—' }}</span>
              </div>
              <div class="tuv__fact">
                <span class="tuv__fact-label">Periode (årsnorm)</span>
                <span class="tuv__fact-value">{{ periodLabel }}</span>
              </div>
              <div class="tuv__fact">
                <span class="tuv__fact-label">Bruttotid</span>
                <span class="tuv__fact-value">{{ yearNormHours != null ? formatDecimal(yearNormHours) : '—' }}</span>
              </div>
            </div>

            <div class="do-form-row">
              <div class="d-input-group">
                <label class="d-label">Lønkode</label>
                <select v-model="salaryCodeId" class="height-34 do-select width-100">
                  <option :value="null"></option>
                  <option v-for="salaryCode in selectableSalaryCodes" :key="salaryCode.id" :value="salaryCode.id">
                    {{ salaryCode.vSupplementTitle || salaryCode.code }}
                  </option>
                </select>
              </div>
              <div class="d-input-group" v-if="salarySystem !== 'SD'">
                <label class="d-label">Omkostningssted</label>
                <select v-model="costCenterId" class="height-34 do-select width-100">
                  <option :value="null"></option>
                  <option v-for="costCenter in costCenters" :key="costCenter.id" :value="costCenter.id">
                    {{ costCenterLabel(costCenter) }}
                  </option>
                </select>
              </div>
            </div>

            <div class="do-form-row">
              <div class="d-input-group">
                <label class="d-label">Skoledage i skoleåret</label>
                <input type="number" min="0" inputmode="numeric" class="d-input" v-model="schoolDaysInYear" placeholder="fx 200">
              </div>
              <div class="d-input-group">
                <label class="d-label">Skoledage i periode</label>
                <input type="number" min="0" inputmode="numeric" class="d-input" v-model="schoolDaysInPeriod" placeholder="fx 46">
              </div>
              <div class="d-input-group">
                <label class="d-label">UV-grænse</label>
                <input type="text" inputmode="decimal" class="d-input" v-model="graense" placeholder="750">
              </div>
            </div>

            <div class="d-input-group">
              <label class="d-label">Justeret timetal</label>
              <input type="text" inputmode="decimal" class="d-input" v-model="adjustedHours" placeholder="Total UV-tid for perioden">
              <p class="tuv__hint">
                Som standard tæller <strong>alt tildelt</strong> UV-tid med. Hvis kun det skemalagte for perioden skal
                tælle med, så ret timetallet til det.
              </p>
              <div class="tuv__scheduled">
                <span>
                  Skemalagt undervisning i perioden:
                  <strong v-if="!isLoadingScheduled">{{ scheduledInPeriod != null ? formatDecimal(scheduledInPeriod) + ' timer' : 'ukendt' }}</strong>
                  <em v-else>henter…</em>
                </span>
                <button
                  v-if="!isLoadingScheduled && scheduledInPeriod != null"
                  type="button"
                  class="tuv__use-scheduled"
                  @click="useScheduled">
                  Brug skemalagt
                </button>
              </div>
            </div>

            <div class="tuv__result" v-if="isCalcValid">
              <div class="tuv__result-steps">
                <div class="tuv__result-row">
                  <span>UV-timer omregnet til fuld tid</span>
                  <span>{{ formatDecimal(uvOmregnetFuldtid) }}</span>
                </div>
                <div class="tuv__result-row">
                  <span>Overskud over grænse (fuld tid)</span>
                  <span>{{ formatDecimal(surplusFuldtid) }}</span>
                </div>
              </div>
              <div class="tuv__result-total">
                <span>UV-tillæg der udbetales i perioden</span>
                <strong>{{ formatDecimal(resultUnits) }} timer</strong>
              </div>
              <div class="tuv__result-row tuv__result-row--sub" v-if="resultUnits > 0">
                <span>Sendes som fast tillæg til {{ salaryTargetLabel }}</span>
                <span>{{ formatDecimal(unitsPerInstallment) }} enh./md · {{ installments }} {{ installments === 1 ? 'måned' : 'måneder' }}</span>
              </div>
              <p class="tuv__result-note" v-if="resultUnits <= 0">
                UV-tiden er under grænsen i perioden – der er ingen UV-tid at oprette tillæg for.
              </p>
            </div>
          </template>
        </template>
      </div>

      <div class="d-modal__buttons">
        <div class="d-modal__buttons__left"></div>
        <div class="d-modal__buttons__right">
          <button class="do-bttn" @click="$emit('close')">{{ $t('vue2.common.cancel') }}</button>
          <button class="do-bttn do-bttn--submit"
                  :class="{'do-bttn--submit--loading': isSaving}"
                  :disabled="!isValid || isSaving || isLoading"
                  @click="create">
            {{ submitButtonLabel }}
          </button>
        </div>
      </div>
    </div>
  </DModal>
</template>

<script>
import DModal from '../common/DModal.vue'
import Loader from '../common/Loader.vue'
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import moment from 'moment'

export default {
  name: 'TidsbegraensetUvModal',
  components: {DModal, Loader},
  data () {
    return {
      userId: '',
      employmentId: null,
      salaryCodeId: null,
      costCenterId: null,
      schoolDaysInYear: '',
      schoolDaysInPeriod: '',
      adjustedHours: '',
      graense: '750',
      ansaettelsesgradPercent: null,
      periodStart: null,
      periodEnd: null,
      yearNormHours: null,
      totalUvHours: null,
      scheduledInPeriod: null,
      isLoading: false,
      isLoadingScheduled: false,
      isSaving: false
    }
  },
  computed: {
    schoolId () {
      return this.$store.getters.schoolId
    },
    schoolYearId () {
      return this.$store.getters.schoolYearId
    },
    schoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    // Lønsystemet afgør hvor det faste tillæg sendes hen (OPUS for KMD, SD for Silkeborg Data).
    salarySystem () {
      return this.$store.getters.salarySystem
    },
    salaryTargetLabel () {
      return this.salarySystem === 'SD' ? 'SD' : 'OPUS'
    },
    submitButtonLabel () {
      return 'Send til ' + this.salaryTargetLabel
    },
    users () {
      return this.$store.getters.usersSortedOnName || []
    },
    salaryCodes () {
      return this.$store.getters.salaryCodesSorted || []
    },
    // KMD kræver en lønart (4-cifret lønkode), mens SD kræver et tillægsnummer. Vis kun de
    // lønkoder der kan bæres af det aktive lønsystem som fast tillæg.
    selectableSalaryCodes () {
      const isSd = this.salarySystem === 'SD'
      return this.salaryCodes.filter(salaryCode => isSd ? salaryCode.tillaegsnummer : salaryCode.loenart)
    },
    costCenters () {
      return this.$store.getters.costCenters || []
    },
    employmentsInUserIdHash () {
      return this.$store.getters.employmentsInUserIdHash || {}
    },
    employmentTemplates () {
      return this.$store.getters.employmentTemplatesSorted || []
    },
    // Ansættelser med stillingskategori og medarbejdernummer – de eneste der kan bære et fast tillæg.
    // For KMD bruges OPUS-ansættelser, for SD bruges ikke-OPUS-ansættelser (jf. Faste tillæg-oversigterne).
    userEmployments () {
      if (!this.userId) return []
      const isSd = this.salarySystem === 'SD'
      const emps = this.employmentsInUserIdHash[this.userId] || []
      return emps
        .filter(emp => {
          const sourceOk = isSd ? emp.source !== 'opus' : (!emp.source || emp.source === 'opus')
          return sourceOk &&
            emp.employment_template_id != null &&
            emp.employment_number && String(emp.employment_number).trim() !== ''
        })
        .map(emp => ({
          id: emp.id,
          employment_template_id: emp.employment_template_id,
          label: (emp.employment_number ? emp.employment_number + ' - ' : '') + (emp.source_title || emp.title || 'Ansættelse')
        }))
    },
    periodLabel () {
      if (!this.periodStart || !this.periodEnd) return '—'
      return moment(this.periodStart).format('DD-MM-YYYY') + ' til ' + moment(this.periodEnd).format('DD-MM-YYYY')
    },
    g () {
      return this.ansaettelsesgradPercent != null ? this.ansaettelsesgradPercent / 100 : null
    },
    uvOmregnetFuldtid () {
      const t = this.parseNumber(this.adjustedHours)
      const n = this.parseNumber(this.schoolDaysInPeriod)
      const big = this.parseNumber(this.schoolDaysInYear)
      if (t == null || n == null || big == null || !this.g || n === 0) return null
      return (t / this.g) * (big / n)
    },
    surplusFuldtid () {
      if (this.uvOmregnetFuldtid == null) return null
      const limit = this.parseNumber(this.graense)
      return this.uvOmregnetFuldtid - (limit != null ? limit : 0)
    },
    // Slutresultat omregnet tilbage til perioden: overskud × ansættelsesgrad × (skoledage i periode / skoledage i året).
    resultUnits () {
      if (this.surplusFuldtid == null) return null
      const n = this.parseNumber(this.schoolDaysInPeriod)
      const big = this.parseNumber(this.schoolDaysInYear)
      if (n == null || big == null || big === 0) return null
      const units = this.surplusFuldtid * this.g * (n / big)
      return units > 0 ? units : 0
    },
    installments () {
      if (!this.periodStart || !this.periodEnd) return 1
      const start = moment(this.periodStart)
      const end = moment(this.periodEnd)
      const months = (end.month() - start.month()) + (end.year() - start.year()) * 12 + 1
      return months > 0 ? months : 1
    },
    // Et fast tillæg udbetales pr. måned, så det samlede UV-tillæg fordeles ligeligt over perioden.
    unitsPerInstallment () {
      if (this.resultUnits == null) return null
      const inst = this.installments
      return inst > 0 ? this.resultUnits / inst : this.resultUnits
    },
    isCalcValid () {
      const daysYear = this.parseNumber(this.schoolDaysInYear)
      const daysPeriod = this.parseNumber(this.schoolDaysInPeriod)
      const hours = this.parseNumber(this.adjustedHours)
      return daysYear != null && daysYear > 0 &&
        daysPeriod != null && daysPeriod > 0 &&
        hours != null && hours >= 0 &&
        this.g != null && this.g > 0
    },
    isValid () {
      return !!this.userId && !!this.employmentId && !!this.salaryCodeId && this.isCalcValid &&
        this.resultUnits != null && this.resultUnits > 0 &&
        this.yearNormHours != null &&
        !!this.periodStart && !!this.periodEnd
    }
  },
  methods: {
    parseNumber (value) {
      if (value === '' || value == null) return null
      const parsed = parseFloat(String(value).replace(',', '.'))
      return isNaN(parsed) ? null : parsed
    },
    formatDecimal (value) {
      const num = parseFloat(value)
      return isNaN(num) ? '—' : num.toFixed(2).replace('.', ',')
    },
    formatPercent (value) {
      const num = parseFloat(value)
      return isNaN(num) ? '—' : num.toFixed(2).replace('.', ',') + ' %'
    },
    costCenterLabel (costCenter) {
      const code = costCenter.code != null ? String(costCenter.code) : ''
      const name = costCenter.name || ''
      if (code && name && code !== name) return code + ' - ' + name
      return name || code
    },
    normValue (norm, title) {
      const found = (norm || []).find(item => item.title === title)
      return found ? found.value : null
    },
    useScheduled () {
      if (this.scheduledInPeriod != null) {
        this.adjustedHours = String(this.scheduledInPeriod)
      }
    },
    onUserChange () {
      this.resetUserData()
      this.employmentId = null
      if (this.userId) {
        this.readUserYear()
        this.$nextTick(() => {
          if (this.userEmployments.length === 1) {
            this.employmentId = this.userEmployments[0].id
            this.onEmploymentChange()
          }
        })
      }
    },
    // UV-grænsen følger stillingskategorien (hour_limit), så den passer til den valgte ansættelse. Falder tilbage til 750.
    onEmploymentChange () {
      const emp = this.userEmployments.find(item => item.id === this.employmentId)
      if (!emp || emp.employment_template_id == null) return
      const template = this.employmentTemplates.find(tmpl => tmpl.id === emp.employment_template_id)
      if (template && template.hour_limit != null && template.hour_limit !== '') {
        this.graense = String(parseFloat(template.hour_limit))
      }
    },
    resetUserData () {
      this.ansaettelsesgradPercent = null
      this.periodStart = null
      this.periodEnd = null
      this.yearNormHours = null
      this.totalUvHours = null
      this.scheduledInPeriod = null
      this.adjustedHours = ''
    },
    readUserYear () {
      this.isLoading = true
      docendo.schoolUserYearsList(this.schoolId, this.schoolYearId, this.userId, success => {
        const userYear = success && success[0] ? success[0] : null
        const norm = userYear && userYear.norm ? userYear.norm : []
        const grad = this.normValue(norm, 'hidden_ansættelsesgrad')
        this.ansaettelsesgradPercent = grad != null ? parseFloat(grad) : null
        this.periodStart = this.normValue(norm, 'hidden_periode_start')
        this.periodEnd = this.normValue(norm, 'hidden_periode_end')
        const brutto = this.normValue(norm, 'Bruttotid')
        this.yearNormHours = brutto != null ? parseFloat(brutto) : null
        this.totalUvHours = userYear && userYear.assigned_teaching_hours != null
          ? parseFloat(userYear.assigned_teaching_hours)
          : null
        if (this.adjustedHours === '' && this.totalUvHours != null) {
          this.adjustedHours = String(this.totalUvHours)
        }
        this.isLoading = false
        this.readScheduledHours()
      }, failure => {
        console.log(failure)
        this.isLoading = false
      })
    },
    readScheduledHours () {
      if (!this.userId || !this.periodStart || !this.periodEnd) {
        this.isLoadingScheduled = false
        return
      }
      this.isLoadingScheduled = true
      const fromDate = moment(this.periodStart).format('YYYY-MM-DD')
      const toDate = moment(this.periodEnd).add(1, 'day').format('YYYY-MM-DD')
      docendo.subjectActuals(this.schoolId, this.schoolYearId, this.userId, fromDate, toDate, success => {
        const rows = Array.isArray(success) ? success : []
        const hours = rows
          .filter(row => row.master_type === 'teaching')
          .reduce((sum, row) => sum + (parseFloat(row.actual) || 0), 0)
        this.scheduledInPeriod = hours
        this.isLoadingScheduled = false
      }, failure => {
        console.log(failure)
        this.scheduledInPeriod = null
        this.isLoadingScheduled = false
      })
    },
    // Sender det beregnede UV-tillæg direkte til lønsystemet som et fast (månedligt) tillæg.
    // Det samlede UV-tillæg fordeles på perioden, så enheder pr. måned ganges med satsen i hver lønperiode.
    create () {
      if (!this.isValid || this.isSaving) return
      const unitsPerInstallment = parseFloat(this.unitsPerInstallment.toFixed(4))
      const startDate = moment(this.periodStart).format('YYYY-MM-DD')
      const endDate = moment(this.periodEnd).format('YYYY-MM-DD')
      this.isSaving = true
      if (this.salarySystem === 'SD') {
        // SD: backenden udleder tjenestenummer fra employment_id og tillægsnummer fra salary_code_id.
        const tillaeg = {
          user_id: this.userId,
          salary_code_id: this.salaryCodeId,
          startdato: startDate,
          slutdato: endDate,
          enheder: unitsPerInstallment
        }
        docendo.bulkCreateSilkeborgDataTillaeg(this.schoolId, [this.employmentId], tillaeg, this.onCreated, this.onCreateFailed)
      } else {
        // KMD: backenden udleder lønart/løbenummer fra salary_code_id og omkostningssted fra salary_cost_center_id.
        const recurringPayment = {
          startdato: startDate,
          stopdato: endDate,
          salary_code_id: this.salaryCodeId,
          antal: unitsPerInstallment
        }
        if (this.costCenterId != null) {
          recurringPayment.salary_cost_center_id = this.costCenterId
        }
        docendo.createRecurringPaymentForEmployees(this.schoolId, [this.employmentId], recurringPayment, this.onCreated, this.onCreateFailed)
      }
    },
    onCreated (success) {
      this.isSaving = false
      if (success && success.status === 'error') {
        eventbus.toast = {text: success.message || ('Kunne ikke sende fast tillæg til ' + this.salaryTargetLabel), success: false, position: 'bottomLeft'}
        return
      }
      eventbus.toast = {text: 'Fast tillæg sendt til ' + this.salaryTargetLabel + '. Følg status i Udbakke.', success: true, position: 'bottomLeft'}
      this.$emit('created', success)
    },
    onCreateFailed (failure) {
      console.log(failure)
      this.isSaving = false
      eventbus.toast = {text: failure && failure.message ? failure.message : ('Kunne ikke sende fast tillæg til ' + this.salaryTargetLabel), success: false, position: 'bottomLeft'}
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.d-modal {
  width: 720px;
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
  text-align: left;
}

.d-modal__content {
  padding: 1.5rem;
  max-height: calc(100vh - 52px - 70px);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.tuv__intro {
  margin: 0 0 1.5rem;
  line-height: 1.5;
}

.tuv__facts {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.tuv__fact {
  flex: 1;
  background: #f6f9fc;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.tuv__fact-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #6b7280;
}

.tuv__fact-value {
  font-size: 15px;
  font-weight: 600;
}

.do-form-row {
  display: flex;
  gap: 1rem;
}

.do-form-row > div {
  flex: 1;
}

.d-input-group {
  margin-bottom: 1.5rem;
}

.d-input-group .d-input,
.height-34 {
  height: 34px;
}

.tuv__hint {
  margin: 0.4rem 0 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

.tuv__scheduled {
  margin-top: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #eef6ff;
  border-radius: 8px;
  padding: 0.6rem 0.85rem;
  font-size: 13px;
}

.tuv__use-scheduled {
  border: none;
  background: none;
  padding: 0;
  color: var(--primary, #468AD7);
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.tuv__use-scheduled:hover {
  text-decoration: underline;
}

.tuv__result {
  background: #f6f9fc;
  border-radius: 8px;
  padding: 1rem;
}

.tuv__result-steps {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 13px;
  color: #4b5563;
}

.tuv__result-row {
  display: flex;
  justify-content: space-between;
}

.tuv__result-row--sub {
  margin-top: 0.5rem;
  font-size: 13px;
  color: #4b5563;
}

.tuv__result-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e3e6ea;
  font-size: 15px;
}

.tuv__result-note {
  margin: 0.6rem 0 0;
  font-size: 12px;
  color: #a26206;
}

.d-modal__buttons {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #eee;
  margin: 0;
}

.d-modal__buttons > div {
  display: flex;
  align-items: center;
  gap: 1rem;
}
</style>



// WEBPACK FOOTER //
// TidsbegraensetUvModal.vue?7cfd23ca